import React from 'react';
import { motion } from 'framer-motion';
import Footer from './Footer';

const MapsRideHelper = () => {
  return (
    <div className="about-page policy-page ride-helper-page">
      <div className="about-inner">

        <motion.section
          className="about-intro-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="about-main-title">MAPS RIDE HELPER</h1>
          <p className="about-main-subtitle">
            How Panda opens Uber or Rapido from Google Maps with your destination already filled in.
          </p>
        </motion.section>

        <hr className="section-divider" />

        <motion.section
          className="policy-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="policy-article">
            <p>
              Maps Ride Helper is a feature of <strong>Panda</strong> (package <code>com.blurr.voice</code>), an Android
              accessibility app from Steepwind Labs. Panda is declared to Google Play as an accessibility tool.
            </p>

            <h2>What it does</h2>
            <p>
              When you look at a place in Google Maps, Panda shows a small row of buttons under the Maps search bar:{' '}
              <strong>Uber</strong> and/or <strong>Rapido</strong>. Only ride apps that are installed on your phone
              are shown.
            </p>
            <p>
              Tap one, and that ride app opens with your destination already filled in. You do not have to type the
              address again.
            </p>

            <h2>Who it is for</h2>
            <p>
              Typing an address a second time is slow and tiring for many people, including people who:
            </p>
            <ul>
              <li>have motor impairments, tremor, or limited dexterity</li>
              <li>have low vision</li>
              <li>use a screen reader</li>
            </ul>
            <p>
              Maps Ride Helper turns that into one tap.
            </p>

            <h2>How Panda reads the place</h2>
            <p>
              Panda reads the place name and address from the text Google Maps shows on screen, using the Android
              Accessibility Service. This happens on your phone. Panda <strong>never</strong> reads your GPS or
              location in the background.
            </p>

            <h2>What is sent, and to whom</h2>
            <ul>
              <li>
                <strong>Uber:</strong> Panda passes the destination address to the Uber app on your phone.
              </li>
              <li>
                <strong>Rapido:</strong> Rapido needs map coordinates. If Google Maps shows them (for example, for a
                dropped pin), Panda uses them directly. If not, Panda sends the destination address to Panda&apos;s
                server to look up its coordinates (geocoding), then passes them to the Rapido app.
              </li>
            </ul>
            <p>
              Nothing else is sent. No location history and no screen content.
            </p>

            <h2>Nothing is booked for you</h2>
            <p>
              Panda only opens the ride app with the destination filled in. It does not book anything. You always
              check and confirm the ride yourself inside Uber or Rapido.
            </p>

            <h2>You stay in control</h2>
            <p>
              Maps Ride Helper is on by default for Panda users. After you tap Uber or Rapido, a small notice says{' '}
              <strong>&ldquo;Ride opened by Panda&rdquo;</strong>. It has three options:
            </p>
            <ul>
              <li><strong>Keep:</strong> leave the feature on.</li>
              <li><strong>Disable:</strong> turn the feature off straight away.</li>
              <li><strong>Know more:</strong> open this page.</li>
            </ul>
            <p>
              You can also turn off Panda&apos;s accessibility service at any time in Android{' '}
              <strong>Settings &rarr; Accessibility</strong>.
            </p>

            <h2>Not affiliated</h2>
            <p>
              Panda and Steepwind Labs are not affiliated with, endorsed by, or sponsored by Uber, Rapido, or Google.
              Their names are used only to say which apps this feature works with.
            </p>

            <h2>Questions</h2>

            <h3>Does Panda book rides for me?</h3>
            <p>
              No. Panda opens Uber or Rapido with the destination filled in. You confirm the ride in that app.
            </p>

            <h3>Does Panda track my location?</h3>
            <p>
              No. Panda does not read your GPS or location in the background. It only reads the place Google Maps is
              showing on screen.
            </p>

            <h3>What is sent to Panda&apos;s server?</h3>
            <p>
              Only the destination address, and only for Rapido when Google Maps does not show coordinates. It is
              used to look up the coordinates Rapido needs.
            </p>

            <h3>How do I turn it off?</h3>
            <p>
              Tap <strong>Disable</strong> on the &ldquo;Ride opened by Panda&rdquo; notice. Or turn off
              Panda&apos;s accessibility service in Android <strong>Settings &rarr; Accessibility</strong>.
            </p>

            <h2>Contact</h2>
            <p>
              Questions or feedback:{' '}
              <a href="mailto:support@heypanda.org" className="policy-link">
                support@heypanda.org
              </a>
              . Read our full{' '}
              <a href="/privacy-policy" className="policy-link">
                privacy policy
              </a>
              .
            </p>
          </div>
        </motion.section>
      </div>

      <Footer />

      <style jsx>{`
        .policy-page {
          background: transparent;
        }

        .policy-article {
          max-width: 800px;
          margin: 0 auto;
        }

        .policy-article h2 {
          font-size: 1.5rem;
          color: var(--primary-color);
          margin-top: 40px;
          margin-bottom: 20px;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .policy-article h3 {
          font-size: 1.15rem;
          color: var(--text-primary);
          margin-top: 24px;
          margin-bottom: 12px;
        }

        .policy-article p {
          font-size: 1.05rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 16px;
        }

        .policy-article ul {
          margin-bottom: 24px;
          padding-left: 24px;
        }

        .policy-article li {
          font-size: 1.05rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .policy-article strong {
          color: var(--text-primary);
        }

        .policy-article code {
          background: rgba(255, 255, 255, 0.08);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          color: var(--primary-color);
        }

        .ride-helper-page .policy-link {
          color: var(--primary-color);
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        .ride-helper-page .policy-link:hover,
        .ride-helper-page .policy-link:focus-visible {
          color: var(--text-primary);
        }

        .ride-helper-page .policy-link:focus-visible {
          outline: 2px solid var(--primary-color);
          outline-offset: 2px;
        }

        @media (max-width: 768px) {
          .policy-article h2 {
            font-size: 1.25rem;
          }
          .policy-article h3 {
            font-size: 1.05rem;
          }
          .policy-article p,
          .policy-article li {
            font-size: 0.95rem;
          }
        }
      `}</style>
    </div>
  );
};

export default MapsRideHelper;
